import { ReactElement, useState, useRef, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'

import Dropdown from 'components/v2/Dropdown'
import List from 'components/v2/List'
import { DotsIcon } from 'components/Icons/DotsIcon'
import IconButton from 'components/v2/IconButton'
import { usePlanContext } from 'features/RoutingProjects/containers/PlanContainer'
import { useApolloClient, useMutation } from '@apollo/client'
import {
  CALCULATE_PLAN_COSTS,
  LOCK_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE,
  START_ROUTING_PROJECT_PLAN_EXPORT
} from 'features/RoutingProjects/mutations'
import { IRoutingProjectModel, IRoutingProjectPlanModel } from 'features/RoutingProjects/types'
import { EditPlanDatasetModal } from 'features/RoutingProjects/components/EditPlanDatasetModal'
import { WarningIcon } from 'components/Icons'
import { GET_ROUTING_PROJECT_PLAN } from 'features/RoutingProjects/queries'
import {
  freezeAllRoutes,
  getOptimizationsInputToLockAllRoutes
} from 'features/RoutingProjects/components/PlanActions/utils'

interface IPlanActionsProps {
  routingProjectModel?: IRoutingProjectModel
  planModel?: IRoutingProjectPlanModel
}

export const PlanActions = ({ planModel, routingProjectModel }: IPlanActionsProps): ReactElement => {
  const client = useApolloClient()
  const { isExportingPlan, setIsExportingPlan, setHasStartedPlanExport, setIsStartingPlanExport, setPlanModel } =
    usePlanContext()
  const [isOpenDropdown, setOpenDropdown] = useState(false)
  const [isOpenEditPlanDatasetModal, setIsOpenEditPlanDatasetModal] = useState(false)
  const [isCalculatingCosts, setIsCalculatingCosts] = useState(false)
  const calculateCostsTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [startPlanExport] = useMutation(START_ROUTING_PROJECT_PLAN_EXPORT)
  const [calculatePlanCosts, { error: calculatePlanCostsError }] = useMutation(CALCULATE_PLAN_COSTS)
  const [lockRoute] = useMutation(LOCK_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (calculateCostsTimeoutRef.current) {
        clearTimeout(calculateCostsTimeoutRef.current)
      }
    }
  }, [])

  const toggleDropdown = (event: React.MouseEvent): void => {
    event.stopPropagation()
    setOpenDropdown((prev) => !prev)
  }

  const handleStartPlanExport = async (event: React.MouseEvent): Promise<void> => {
    event.stopPropagation()
    if (planModel?.id && routingProjectModel?.id) {
      try {
        setIsStartingPlanExport(true)
        setOpenDropdown(false)
        await startPlanExport({
          variables: {
            input: {
              id: routingProjectModel?.id,
              plan: {
                id: planModel?.id
              }
            }
          }
        })
        setIsStartingPlanExport(false)
        setHasStartedPlanExport(true)
        setIsExportingPlan(true)
      } catch (ex) {
        console.error('Error starting plan export:', ex)
        setHasStartedPlanExport(false)
        setIsExportingPlan(false)
        setIsStartingPlanExport(false)
      }
    }
  }

  const handleCalculatePlanCosts = async (event: React.MouseEvent): Promise<void> => {
    event.stopPropagation()

    if (planModel?.id && routingProjectModel?.id) {
      try {
        setIsCalculatingCosts(true)

        await calculatePlanCosts({
          variables: {
            input: {
              id: routingProjectModel?.id,
              plan: {
                id: planModel?.id
              }
            }
          }
        })

        // Clear any existing timeout
        if (calculateCostsTimeoutRef.current) {
          clearTimeout(calculateCostsTimeoutRef.current)
        }

        // Wait 2 seconds before refetching
        calculateCostsTimeoutRef.current = setTimeout(() => {
          client.refetchQueries({
            include: [GET_ROUTING_PROJECT_PLAN]
          })
          setIsCalculatingCosts(false)
          setOpenDropdown(false)
        }, 2000)
      } catch (error) {
        console.error('Error calculating plan costs:', error)
        setIsCalculatingCosts(false)
      }
    }
  }

  const handleFreezeAllRoutes = async (event: React.MouseEvent): Promise<void> => {
    event.stopPropagation()
    if (planModel?.id && routingProjectModel?.id) {
      try {
        const newPlanModel = freezeAllRoutes(planModel)
        setPlanModel(newPlanModel)
        setOpenDropdown(false)
        await lockRoute({
          variables: {
            input: {
              id: routingProjectModel.id,
              plan: {
                id: planModel.id,
                optimizations: getOptimizationsInputToLockAllRoutes(newPlanModel.optimizations)
              }
            }
          }
        })
      } catch (error) {
        console.error('Error freezing all routes:', error)
      }
    }
  }

  const hasValidPlanAndProject = Boolean(planModel?.id && routingProjectModel?.id)
  const planHasRoutes = planModel?.optimizations.some((optimization) => optimization.routes.length > 0)
  const isExportPlanDisabled = isExportingPlan || !planHasRoutes || !hasValidPlanAndProject
  const isAddDatasetDisabled = !hasValidPlanAndProject
  const isCalculatePlanCostsDisabled = !hasValidPlanAndProject
  const isFreezeAllRoutesDisabled = !hasValidPlanAndProject || !planHasRoutes

  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator onClickAway={(): void => setOpenDropdown(false)}>
        <IconButton icon={<DotsIcon />} onClick={toggleDropdown} noBorder smallPadding />
        <Dropdown.Content open={isOpenDropdown} noIndicator right>
          <List.Container>
            <List.Item action onClick={handleStartPlanExport} disabled={isExportPlanDisabled}>
              <FormattedMessage id="routingProjectSidebar.plansList.exportPlan" defaultMessage="Download plan" />
            </List.Item>
            <List.Item action onClick={(): void => setIsOpenEditPlanDatasetModal(true)} disabled={isAddDatasetDisabled}>
              <FormattedMessage id="routingProjectSidebar.plansList.addDataset" defaultMessage="Add dataset" />
            </List.Item>
            <List.Item
              action
              onClick={handleCalculatePlanCosts}
              loading={isCalculatingCosts}
              disabled={isCalculatePlanCostsDisabled}
            >
              <FormattedMessage
                id="routingProjectSidebar.plansList.calculatePlanCosts"
                defaultMessage="Calculate operational cost"
              />
              {calculatePlanCostsError && <WarningIcon red />}
            </List.Item>
            <List.Item action onClick={handleFreezeAllRoutes} disabled={isFreezeAllRoutesDisabled}>
              <FormattedMessage
                id="routingProjectSidebar.plansList.freezeAllRoutes"
                defaultMessage="Freeze all routes"
              />
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isOpenEditPlanDatasetModal && (
        <EditPlanDatasetModal
          routingProjectId={routingProjectModel?.id as string}
          planModel={planModel as IRoutingProjectPlanModel}
          closeModal={(): void => setIsOpenEditPlanDatasetModal(false)}
          availableDatasets={
            routingProjectModel?.datasets.map((dataset) => ({
              id: dataset.id,
              name: dataset.name
            })) || []
          }
        />
      )}
    </>
  )
}
